// Added by Sejune Oh on 2019-09-04
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC } from 'react'
import { withStyles, Theme, createStyles, WithStyles } from '@material-ui/core'
import classNames from 'classnames'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ReactEmptyEvent } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { CloseIcon } from '../../_primes'
import { NotiLevel } from './NotiBarContainer'
import LegacyAppIcon from '@/legacy-polymer/LegacyAppIcon'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface NotiBarFCProps extends WithStyles<typeof styles> {
    notiLevel: NotiLevel
    notiMsg: string
    notiLink?: string
    onLinkClick: ReactEmptyEvent
    onCloseBtnClick: ReactEmptyEvent
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const NotiBar: FC<NotiBarFCProps> = ({
    classes,
    notiLevel,
    notiMsg,
    notiLink = '',
    onLinkClick,
    onCloseBtnClick,
}) => {
    const isLinkValid = notiLink !== ''
    let iconComponent

    switch (notiLevel) {
        case NotiLevel.none:
            iconComponent = null
            break
        case NotiLevel.warn:
            iconComponent = (
                <span className={classes.NotiIconWrapper}>
                    <LegacyAppIcon  
                        iconSize={26}
                        iconName={'cl:noti-info'}
                        title="Warn"
                        style={{ fill: '#ffC107' }}
                    />
                </span>
            )
            break
        case NotiLevel.err:
            iconComponent = (
                <span className={classes.NotiIconWrapper}>
                    <LegacyAppIcon  
                        iconSize={26}
                        iconName={'cl:noti-warning'}
                        title="Err"
                        style={{ fill: '#ff2822' }}
                    />
                </span>
            )
            break
        case NotiLevel.info:
            iconComponent = (
                <span className={classes.NotiIconWrapper}>
                    <span 
                        className={classes.NotiInfoIconWrapper} 
                        style={{ backgroundImage: `url(/images/noti_aview.png)` }} 
                    />
                </span>
            )
            break
    }

    return (
        <div
            className={classNames(classes.NotiWrapper, {
                [classes.NotiActive]: notiLevel !== NotiLevel.none,
            })}
        >
            {iconComponent}
            {isLinkValid ? (
                <span className={classNames(classes.NotiInfoWrapper, classes.NotiLinkWrapper)} onClick={onLinkClick}>
                    {notiMsg} ({notiLink})
                </span>
            ) : (
                    <span className={classes.NotiInfoWrapper}>{notiMsg}</span>
                )}

            <span className={classes.NotiCloseWrapper} onClick={onCloseBtnClick}>
                <CloseIcon></CloseIcon>
            </span>
        </div>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        NotiWrapper: {
            position: 'fixed',
            top: '-48px',
            padding: `${theme.spacing() / 2}px ${theme.spacing() + 26}px`,
            height: '32px',
            transition: 'top 0.669s',
            left: '0',
            right: '0',
            width: 'fit-content',
            minWidth: '30vw',
            maxWidth: '100vw',
            margin: 'auto',
            lineHeight: '20px',
            color: '#000',
            fontSize: '0.9rem',
            zIndex: 2,
            boxShadow:
                '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
            borderBottomLeftRadius: `6px`,
            borderBottomRightRadius: `6px`,
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
        },
        NotiActive: {
            top: '0px',
        },
        NotiLinkWrapper: {
            cursor: 'pointer',
            borderBottom: '1px solid #000',
        },
        NotiIconWrapper: {
            position: 'absolute',
            left: `${theme.spacing() / 2}px`,
            width: '26px',
            height: '26px',
            top: 0,
            bottom: 0,
            margin: 'auto',
        },
        NotiInfoIconWrapper: {
            width: '26px',
            height: '26px',
            display: 'block',
            backgroundSize: 'contain',
        },
        NotiCloseWrapper: {
            position: 'absolute',
            right: `${theme.spacing() / 2}px`,
            width: '26px',
            height: '26px',
            top: 0,
            bottom: 0,
            margin: 'auto',
            cursor: 'pointer',
        },
        NotiInfoWrapper: {
            lineHeight: '1.2rem',
            height: '1.2rem',
        },
    })

export default withStyles(styles, { withTheme: true })(NotiBar)
